import { gql } from '@apollo/client'

// Server-side Brandfetch proxy (rolla-db's getBrandInfo, src/graphQL/utils/miscellaneousUtils.ts)
// — calls Brandfetch's GET /v2/brands/{domain} with our stored API key and passes the response
// straight through as `obj`. Used by SignupPage's Company Details step to prefill name/logo/hero
// image from a company URL. `allUsersAllowed` server-side, so it's safe to call before any team
// exists.
export const GET_BRAND_INFO = gql`
  query GetBrandInfo($domain: String!) {
    getBrandInfo(domain: $domain) {
      returnCode
      message
      obj
    }
  }
`

export const GET_TEAM = gql`
  query GetTeam($id: ID!) {
    getTeam(id: $id) {
      id
      name
      teamLogoURL
      websiteURL
      tags
      slug
      customPushNotificationAccess
      maxActiveTemplates
      maxTeamStorageMB
      teamParentId
      planName
      planExpiresAt
      teamBranding {
        id
        name
        description
        signInOptions
        teamBrandingAsset {
          id
          logoNoPoweredByLargeURL
          logoDarkPoweredByLargeURL
          logoLightPoweredByLargeURL
          heroImageURL
        }
      }
    }
  }
`

export const GET_TEAM_STORAGE = gql`
  query GetTeamStorage($teamId: ID!) {
    getTeamStorage(teamId: $teamId) {
      count
      totalTeamStorageMB
      rows {
        profileId
        tableName
        totalStorageMB
      }
    }
  }
`

export const GET_TEAMS_UNDER_MAIN_TEAM = gql`
  query GetTeamsUnderMainTeam($teamParentId: ID, $limit: Int) {
    getTeams(teamParentId: $teamParentId, limit: $limit) {
      count
      rows {
        id
        name
        teamLogoURL
        teamParentId
        description
        createdAt
        updatedAt
        slug
        profileCount
        teamProjectTemplates {
          projectTemplateId
          projectTemplate {
            id
            name
            enabled
            thumbnailLarge
            cardViewThumbnailURL
            fullscreenThumbnailURL
            expiresAt
            createdAt
            projectTemplateOrientation
            projectTemplateIncentiveLinks {
              incentiveId
            }
          }
        }
      }
    }
  }
`

export const GET_SELECTED_TEAM_ADMINS = gql`
  query GetSelectedTeamAdmins($teamId: ID!) {
    getTeamAdmins(teamId: $teamId) {
      rows {
        id
        profileId
        role
        profile {
          id
          firstName
          lastName
          photo
          emailAddresses {
            emailAddress
            primary
          }
          createdAt
        }
      }
    }
  }
`

export const GET_ALL_TEAMS = gql`
  query GetAllTeams($searchStr: String, $offset: Int, $limit: Int) {
    getTeams(searchStr: $searchStr, offset: $offset, limit: $limit) {
      count
      rows {
        id
        name
        teamLogoURL
        tags
        slug
        customPushNotificationAccess
        teamParentId
        planName
        createdAt
        maxActiveTemplates
        maxSeats
      }
    }
  }
`

// Summary-only variant (BAC-1765) — for callers that only need
// creditUsageSummary.totalAdjustedCreditsUsed and never render rows/calculated. The full
// GET_TEAM_CREDIT_USAGES_BY_TEAM_ID document's rows/calculated fields make the backend run
// several extra queries (including a deep-include findMany) even when nothing reads them —
// requesting only what's actually used lets it skip that work entirely. Use
// GET_TEAM_CREDIT_USAGES_BY_TEAM_ID instead for anything that needs the row list or filter
// dropdown options (e.g. CreditTrackerModal).
export const GET_TEAM_CREDIT_USAGE_SUMMARY = gql`
  query GetTeamCreditUsageSummary($teamId: ID!) {
    getTeamCreditUsagesByTeamId(teamId: $teamId) {
      returnCode
      message
      creditUsageSummary {
        totalAdjustedCreditsUsed
        teamType
      }
    }
  }
`

export const GET_TEAM_CREDIT_USAGES_BY_TEAM_ID = gql`
  query GetTeamCreditUsagesByTeamId(
    $teamId: ID!
    $creditType: String
    $projectTemplateId: ID
    $brokerageTeamId: ID
    $createdAfterDate: DateTime
    $createdBeforeDate: DateTime
    $limit: Int
    $offset: Int
  ) {
    getTeamCreditUsagesByTeamId(
      teamId: $teamId
      creditType: $creditType
      projectTemplateId: $projectTemplateId
      brokerageTeamId: $brokerageTeamId
      createdAfterDate: $createdAfterDate
      createdBeforeDate: $createdBeforeDate
      limit: $limit
      offset: $offset
    ) {
      returnCode
      message
      count
      creditUsageSummary {
        totalAdjustedCreditsUsed
        teamType
      }
      calculated {
        projectTemplates {
          id
          name
        }
        brokerageTeams {
          id
          name
        }
      }
      rows {
        id
        teamCreditId
        projectId
        quantity
        note
        createdAt
        updatedAt
        deletedAt
        teamCredit {
          id
          creditType
        }
        instantEdit {
          name
          source
        }
        project {
          id
          name
          projectTemplate {
            id
            name
          }
          projectOwnerProfile {
            id
            firstName
            lastName
            photo
            parentTeam {
              parentTeam {
                name
              }
            }
          }
        }
      }
    }
  }
`

// Credit Tracker's "Export CSV" (BAC-1744) — same filters as GET_TEAM_CREDIT_USAGES_BY_TEAM_ID,
// and async: true hands the whole export off to the backend, which emails the admin a download
// link once it's ready instead of returning rows here.
export const EXPORT_TEAM_CREDIT_USAGES_BY_TEAM_ID_CSV = gql`
  query ExportTeamCreditUsagesByTeamIdCsv(
    $teamId: ID!
    $creditType: String
    $projectTemplateId: ID
    $brokerageTeamId: ID
    $createdAfterDate: DateTime
    $createdBeforeDate: DateTime
  ) {
    getTeamCreditUsagesByTeamId(
      teamId: $teamId
      creditType: $creditType
      projectTemplateId: $projectTemplateId
      brokerageTeamId: $brokerageTeamId
      createdAfterDate: $createdAfterDate
      createdBeforeDate: $createdBeforeDate
      async: true
    ) {
      returnCode
      message
    }
  }
`

export const GET_TEAM_CREDITS = gql`
  query GetTeamCredits($teamId: ID!) {
    getTeamCredits(teamId: $teamId) {
      count
      rows {
        id
        creditType
        creditPlan
        quantity
        expiresAt
        teamCreditUsage
      }
    }
  }
`

export const GET_BEST_AVAILABLE_TEAM_CREDIT_WITH_LIMITS = gql`
  query GetBestAvailableTeamCreditWithLimits($teamId: ID!, $creditType: String!, $projectId: ID) {
    getBestAvailableTeamCreditWithLimits(teamId: $teamId, creditType: $creditType, projectId: $projectId) {
      id
    }
  }
`

export const GET_TEAM_ADMIN_INVITE_LINKS = gql`
  query GetTeamAdminInviteLinks($teamId: ID!) {
    getTeamAdminInviteLinks(teamId: $teamId) {
      id
      code
      createdAt
      expiresAt
      revoked
      redemptionCount
      role
      inviterProfile {
        id
        firstName
        lastName
      }
    }
  }
`

// Public — called with the anonymous session's token as transport auth (see lib/firebase.ts's
// ensureAnonymousSignIn), before the visitor has signed in for real.
export const GET_TEAM_ADMIN_INVITE_INFO = gql`
  query GetTeamAdminInviteInfo($code: String!) {
    getTeamAdminInviteInfo(code: $code) {
      valid
      reason
      teamName
      teamLogoURL
    }
  }
`
